import { InventoryItemType, getMaxDurability, isTool } from '../items'

export interface InventorySlot {
  type: InventoryItemType
  count: number
  durability?: number
}

export default class Inventory {
  static HOTBAR_SIZE = 9
  static STORAGE_SIZE = 27
  // 36 standard storage slots + 9 crafting input slots + 1 crafting output slot
  static TOTAL_SIZE = 46
  static MAX_STACK = 64
  
  static CRAFTING_START = 36
  static PERSONAL_CRAFTING_END = 39
  static CRAFTING_END = 44
  static CRAFTING_OUTPUT = 45

  slots: (InventorySlot | null)[] = new Array(Inventory.TOTAL_SIZE).fill(null)

  /**
   * Add items to inventory. Tries to stack first, then fills empty slots.
   * Returns the number of items that could NOT be added (overflow).
   */
  addItem(type: InventoryItemType, count: number = 1): number {
    let remaining = count
    const tool = isTool(type)
    const stackMax = tool ? 1 : Inventory.MAX_STACK

    // first pass: stack onto existing slots of the same type (skip tools — each tool has its own durability)
    if (!tool) {
      for (let i = 0; i < 36 && remaining > 0; i++) {
        const slot = this.slots[i]
        if (slot && slot.type === type && slot.count < stackMax) {
          const canAdd = Math.min(remaining, stackMax - slot.count)
          slot.count += canAdd
          remaining -= canAdd
        }
      }
    }

    // second pass: fill empty slots (exclude crafting)
    for (let i = 0; i < 36 && remaining > 0; i++) {
      if (!this.slots[i]) {
        const canAdd = Math.min(remaining, stackMax)
        const newSlot: InventorySlot = { type, count: canAdd }
        if (tool) newSlot.durability = getMaxDurability(type)
        this.slots[i] = newSlot
        remaining -= canAdd
      }
    }

    return remaining
  }

  /**
   * Apply damage to a tool in the given slot. If durability reaches 0,
   * the item is destroyed and the slot cleared. Returns true if the
   * tool broke as a result of this damage.
   */
  damageTool(slotIndex: number, amount: number = 1): boolean {
    const slot = this.slots[slotIndex]
    if (!slot || slot.durability === undefined) return false
    slot.durability -= amount
    if (slot.durability <= 0) {
      this.slots[slotIndex] = null
      return true
    }
    return false
  }

  /**
   * Remove items from a specific slot. Returns true if successful.
   */
  removeItem(slotIndex: number, count: number = 1): boolean {
    const slot = this.slots[slotIndex]
    if (!slot || slot.count < count) return false

    slot.count -= count
    if (slot.count <= 0) {
      this.slots[slotIndex] = null
    }
    return true
  }

  /**
   * Check if a specific block type exists anywhere in inventory.
   */
  hasItem(type: InventoryItemType): boolean {
    return this.slots.some(slot => slot !== null && slot.type === type)
  }

  /**
   * Find the first slot index containing the given block type.
   * Returns -1 if not found.
   */
  findSlot(type: InventoryItemType): number {
    return this.slots.findIndex(slot => slot !== null && slot.type === type)
  }

  /**
   * Find the first hotbar slot (0-8) containing the given type.
   * If not in hotbar, returns the first inventory slot.
   * Returns -1 if not found anywhere.
   */
  findHotbarSlot(type: InventoryItemType): number {
    // check hotbar first
    for (let i = 0; i < Inventory.HOTBAR_SIZE; i++) {
      if (this.slots[i] && this.slots[i]!.type === type) return i
    }
    // fallback to any slot
    return this.findSlot(type)
  }

  /**
   * Get the slot data at index (or null).
   */
  getSlot(index: number): InventorySlot | null {
    return this.slots[index] ?? null
  }

  /**
   * Swap two slots.
   */
  swapSlots(a: number, b: number): void {
    const temp = this.slots[a]
    this.slots[a] = this.slots[b]
    this.slots[b] = temp
  }

  /**
   * Get total count of a specific block type across all slots.
   */
  getItemCount(type: InventoryItemType): number {
    let total = 0
    for (const slot of this.slots) {
      if (slot && slot.type === type) {
        total += slot.count
      }
    }
    return total
  }

  /**
   * Remove one item of a specific type from anywhere in inventory.
   * Prefers hotbar slots. Returns true if successful.
   */
  consumeItem(type: InventoryItemType): boolean {
    // prefer hotbar
    for (let i = 0; i < Inventory.HOTBAR_SIZE; i++) {
      if (this.slots[i] && this.slots[i]!.type === type) {
        return this.removeItem(i, 1)
      }
    }
    // then storage
    for (let i = Inventory.HOTBAR_SIZE; i < 36; i++) {
      if (this.slots[i] && this.slots[i]!.type === type) {
        return this.removeItem(i, 1)
      }
    }
    return false
  }

  /**
   * Clear the entire inventory.
   */
  clear(): void {
    this.slots.fill(null)
  }

  /**
   * Serialize inventory for localStorage.
   */
  serialize(): string {
    return JSON.stringify(this.slots)
  }

  /**
   * Deserialize inventory from localStorage.
   */
  deserialize(data: string): void {
    try {
      const parsed = JSON.parse(data)
      if (Array.isArray(parsed)) {
        if (parsed.length === Inventory.TOTAL_SIZE) {
          this.slots = parsed
        } else if (parsed.length === 41) {
          this.slots = new Array(Inventory.TOTAL_SIZE).fill(null)
          for (let i = 0; i < 40; i++) {
            this.slots[i] = parsed[i]
          }
        }
      }
    } catch {
      // ignore bad data
    }
  }
}
